import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import type { Send } from '../../types/send';
import type { GradingSystemWithGrades } from '../../types/gym';
import { getSendCategory, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

const COLORS = {
  on_sight: '#22c55e',
  flash: '#f97316',
  rotpunk: '#3b82f6',
};

interface GradeDistributionChartProps {
  sends: Send[];
  gradingSystem: GradingSystemWithGrades | null | undefined;
}

export default function GradeDistributionChart({ sends, gradingSystem }: GradeDistributionChartProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    if (!gradingSystem) return [];

    const grades = [...gradingSystem.grades].sort((a, b) => a.value - b.value);
    const gradeBuckets = new Map<string, { on_sight: number; flash: number; rotpunk: number }>();
    for (const g of grades) {
      gradeBuckets.set(g.name, { on_sight: 0, flash: 0, rotpunk: 0 });
    }

    for (const send of sends) {
      const name = send.attributes.climb.difficulty;
      if (!name || !gradeBuckets.has(name)) continue;
      const cat = getSendCategory(send.attributes.send_type);
      gradeBuckets.get(name)![cat]++;
    }

    return grades
      .filter(g => {
        const b = gradeBuckets.get(g.name)!;
        return b.on_sight + b.flash + b.rotpunk > 0;
      })
      .map(g => ({ grade: g.name, ...gradeBuckets.get(g.name)! }));
  }, [sends, gradingSystem]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('stats.gradeDistribution.title')}</CardTitle>
      </CardHeader>
      {data.length === 0 ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="grade" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend
                  formatter={(value) => t(`stats.categories.${value}`)}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="on_sight" name="on_sight" stackId="a" fill={COLORS.on_sight} radius={[0, 0, 0, 0]} />
                <Bar dataKey="flash" name="flash" stackId="a" fill={COLORS.flash} />
                <Bar dataKey="rotpunk" name="rotpunk" stackId="a" fill={COLORS.rotpunk} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
